import { useTranslation } from 'react-i18next';
import { Bot as BotIcon, Building2 } from 'lucide-react';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function WorkspaceSwitcher(): JSX.Element | null {
  const { t } = useTranslation();
  const {
    orgs,
    bots,
    currentOrgId,
    currentBotId,
    currentOrg,
    loading,
    noMemberships,
    setOrg,
    setBot,
  } = useWorkspace();

  if (loading && orgs.length === 0) return null;
  if (noMemberships) {
    return (
      <Badge variant="outline" className="hidden md:inline-flex">
        {t('workspace.noOrg')}
      </Badge>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5">
        <Building2 className="hidden h-4 w-4 text-muted-foreground sm:inline" />
        <Select value={currentOrgId ?? undefined} onValueChange={setOrg}>
          <SelectTrigger className="h-8 w-[160px] text-xs">
            <SelectValue placeholder={t('workspace.orgPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {orgs.map((o) => (
              <SelectItem key={o.orgId} value={o.orgId}>
                <div className="flex items-center gap-2">
                  <span>{o.name}</span>
                  <Badge variant="secondary" className="px-1 py-0 text-[10px]">
                    {o.role}
                  </Badge>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-1.5">
        <BotIcon className="hidden h-4 w-4 text-muted-foreground sm:inline" />
        <Select
          value={currentBotId ?? undefined}
          onValueChange={(v) => setBot(v)}
          disabled={!currentOrgId || bots.length === 0}
        >
          <SelectTrigger className="h-8 w-[160px] text-xs">
            <SelectValue
              placeholder={bots.length === 0 ? t('workspace.noBot') : t('workspace.botPlaceholder')}
            />
          </SelectTrigger>
          <SelectContent>
            {bots.map((b) => (
              <SelectItem key={b.botId} value={b.botId}>
                <div className="flex items-center gap-2">
                  <span>{b.name}</span>
                  {!b.enabled ? (
                    <Badge variant="outline" className="px-1 py-0 text-[10px]">
                      off
                    </Badge>
                  ) : null}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {currentOrg ? (
        <span className="hidden text-[10px] uppercase tracking-wider text-muted-foreground lg:inline">
          {t('workspace.role')} {currentOrg.role}
        </span>
      ) : null}
    </div>
  );
}
